import { Box, Button, ButtonGroup, Checkbox, Dialog, Fab, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select, Stack, TableContainer } from "@mui/material"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Localized, useLocalization } from "@fluent/react"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { loadCodes, loadCompanySettings, loadTypes, CompanySetting } from "./companySettingsApi"
import { CreateEditCompanySettingsDialog } from "./CreateEditCompanySettingsDialog"
import AddIcon from '@mui/icons-material/Add'
import produce from "immer"
import { Company, loadCompanies } from "../../company/companiesSlice"
import SyncIcon from '@mui/icons-material/Sync';
import { If } from "../../../app/If"
import { RestoreDefaultCompanySettingsDialog } from "./RestoreDefaultCompanySettingsDialog"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"

type DialogState =
    | { type: "none" }
    | { type: "createEdit", companySetting: CompanySetting | undefined }
    | { type: "restoreDefault", companySetting: CompanySetting | undefined }

interface CompanySettingsProps {
    companyId?: string;
}

export const CompanySettings = ({ companyId }: CompanySettingsProps) => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const [companySettings, setCompanySettings] = useState<CompanySetting[]>([]);
    const [filteredCompanySettings, setFilteredCompanySettings] = useState<CompanySetting[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [companies, setCompanies] = useState<Company[]>([]);
    const [types, setTypes] = useState<string[]>([]);
    const [codes, setCodes] = useState<string[]>([]);
    const [triggerRefresh, setTriggerRefresh] = useState(false);

    const [company, setCompany] = useState('all');
    const [type, setType] = useState('all');
    const [code, setCode] = useState('all');
    const [hideDefault, setHideDefault] = useState<boolean>(false);

    const [dialog, setDialog] = useState<DialogState>({ type: "none" });

    useEffect(() => {
        if(triggerRefresh) {
            handleRefreshClick();
            setTriggerRefresh(false);
        }
    }, [companySettings]);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadCompanySettings())
            .then(unwrapResult)
            .then(result => {
                const filteredCompanySettings = companyId ? result.filter(f => f.companyId === Number(companyId)) : result;
                setCompanySettings(filteredCompanySettings);
                setFilteredCompanySettings(filteredCompanySettings);
            })
            .catch((error) => console.error(error))
            .finally(() => setIsLoading(false));
        dispatch(loadCompanies())
            .then(unwrapResult)
            .then(setCompanies)
            .catch((error) => console.error(error))
        dispatch(loadTypes())
            .then(unwrapResult)
            .then(setTypes)
            .catch((error) => console.error(error))
        dispatch(loadCodes())
            .then(unwrapResult)
            .then(setCodes)
            .catch((error) => console.error(error))
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback((systemSetting: string | CompanySetting | CompanySetting[]) => {
            setter({ type: "none" })
            setCompanySettings(
                produce((draft) => {
                    if (draft !== undefined) {
                        if(typeof(systemSetting) === "string") return; // tymczasowe rozwiązanie
                        if(Array.isArray(systemSetting)) {
                            draft.push(...systemSetting);
                        } else {
                            const existingIndex = draft.findIndex((group) => group.id === systemSetting.id);

                            if (existingIndex !== -1) {
                                draft[existingIndex] = systemSetting;
                            } else {
                                draft.push(systemSetting);
                            }
                        }
                    }
                })
            )
            setTriggerRefresh(true);
        }, [setter, dispatch])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "createEdit":
                return <CreateEditCompanySettingsDialog
                    mode={state.companySetting ? "edit" : "create"}
                    companySetting={state.companySetting}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />

            case "restoreDefault":
                return <RestoreDefaultCompanySettingsDialog
                    companySetting={state.companySetting}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />
        }
    }

    const handleCompanyChange = (event) => {
        setCompany(event.target.value);
    };

    const handleTypeChange = (event) => {
        setType(event.target.value);
    };

    const handleCodeChange = (event) => {
        setCode(event.target.value);
    };

    const handleHideDefaultChange = (event) => {
        setHideDefault(event.target.checked);
    }

    const handleAddSystemSettingClick = useCallback(() => {
        setDialog({
            type: "createEdit",
            companySetting: undefined
        })
    }, [setDialog])

    const handleEditSystemSettingClick = useCallback((companySetting: CompanySetting) => {
        setDialog({
            type: "createEdit",
            companySetting
        })
    }, [setDialog])

    const handleRestoreDefaultSettingClick = useCallback((companySetting: CompanySetting) => {
        setDialog({
            type: "restoreDefault",
            companySetting
        })
    }, [setDialog])

    const handleRefreshClick = () => {
        const filter = companySettings.filter(item => {
            const companyMatch = company === "all" || String(item.companyId) === company;
            const typeMatch = type === "all" || item.type === type;
            const codeMatch = code === "all" || item.code === code;
            const hideDefaultMatch = hideDefault ? !item.isDefault : true;

            return companyMatch && typeMatch && codeMatch && hideDefaultMatch;
        });

        setFilteredCompanySettings(filter);
    }

    const columns: GridColDef<CompanySetting, any, any>[] = [
        {
            field: 'type',
            headerName: l10n.getString("type", null, "_Typ"),
            flex: 1
        },
        {
            field: 'code',
            headerName: l10n.getString("system-settings-code", null, "_Kod"),
            flex: 1,
        },
        {
            field: 'value',
            headerName: l10n.getString("system-settings-value", null, "_Wartość"),
            flex: 1,
        },
        {
            field: 'isDefault',
            headerName: l10n.getString("default-company-settings-is-default", null, "_Domyślne"),
            flex: 1,
            renderCell: (params) => <Checkbox disabled checked={params.row.isDefault}/>
        },
        {
            field: 'isActive',
            headerName: l10n.getString("system-settings-active", null, "_Aktywne"),
            flex: 1,
            renderCell: (params) => <Checkbox disabled checked={params.row.isActive}/>
        },
        {
            field: 'isFixed',
            headerName: l10n.getString("system-settings-fixed", null, "_Stałe"),
            flex: 1,
            renderCell: (params) => <Checkbox disabled checked={params.row.isFixed}/>
        },
        {
            field: 'createDate',
            headerName: l10n.getString("create-date", null, "_Data utworzenia"),
            flex: 1,
            valueGetter: (_, row) => row.createDate ? new Date(row.createDate).toLocaleString() : null
        },
        {
            field: 'modifiedBy',
            headerName: l10n.getString("modified-by", null, "_Modifikowane przez"),
            flex: 1
        },
        {
            field: 'modifyDate',
            headerName: l10n.getString("modify-date", null, "_Data modyfikacji"),
            flex: 1,
            valueGetter: (_, row) => row.modifyDate ? new Date(row.modifyDate).toLocaleString() : null
        },
        {
            field: 'actions',
            type: 'actions',
            width: 350,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 100 }}
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleEditSystemSettingClick(params.row)}
                        >
                            <Localized id="edit">
                                <span>_Edytuj</span>
                            </Localized>
                        </Button>
                        <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => handleRestoreDefaultSettingClick(params.row)}
                            disabled={params.row.isDefault}
                        >
                            <Localized id="company-settings-restore-default">
                                <span>_Przywróć domyślne</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    if(companyId === undefined) {
        columns.unshift({
            field: 'companyId',
            headerName: l10n.getString("company", null, "_Nazwa firmy"),
            width: 200,
            valueGetter: (_, row) => {
                const company = companies.find(f => f.id === String(row.companyId));
                return company ? company.name : "";
            }
        });
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <If condition={companyId === undefined}>
                <div>
                    <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                        <FormControl>
                            <InputLabel id="select-company-label"><Localized id="company">Firma</Localized></InputLabel>
                            <Select
                                labelId="select-company-label"
                                value={company}
                                onChange={handleCompanyChange}
                                label={<Localized id="company">Firma</Localized>}
                                sx={{ width: '200px' }}
                            >
                                <MenuItem value={'all'}>
                                    <Localized id="all">All</Localized>
                                </MenuItem>
                                {companies.map((option) =>
                                    <MenuItem value={option.id}>{option.name}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel id="select-type-label"><Localized id="type">_Typ</Localized></InputLabel>
                            <Select
                                labelId="select-type-label"
                                value={type}
                                onChange={handleTypeChange}
                                label={<Localized id="type">_Typ</Localized>}
                                sx={{ width: '200px' }}
                            >
                                <MenuItem value={'all'}>
                                    <Localized id="all">All</Localized>
                                </MenuItem>
                                {types.map((option) =>
                                    <MenuItem value={option}>{option}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel id="select-code-label"><Localized id="system-settings-code">_Kod</Localized></InputLabel>
                            <Select
                                labelId="select-code-label"
                                value={code}
                                onChange={handleCodeChange}
                                label={<Localized id="system-settings-code">_Kod</Localized>}
                                sx={{ width: '200px' }}
                            >
                                <MenuItem value={'all'}>
                                    <Localized id="all">All</Localized>
                                </MenuItem>
                                {codes.map((option) =>
                                    <MenuItem value={option}>{option}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hideDefault}
                                    onChange={handleHideDefaultChange} />}
                            label={<Localized id="default-company-settings-hide-default"><span>_Ukryj domyślne</span></Localized>}
                        />
                        <IconButton onClick={handleRefreshClick}>
                            <SyncIcon />
                        </IconButton>
                    </Stack>
                </div>
            </If>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: companyId !== undefined ? 'calc(100vh - 370px)' : 'calc(100vh - 350px)' }}
                    rows={filteredCompanySettings}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <If condition={companyId === undefined}>
                    <Box sx={{
                        display: 'flex',
                    }}>
                        <Fab sx={{
                            margin: 1,
                        }}
                        onClick={() => handleAddSystemSettingClick()} color="secondary" size="small" >
                            <AddIcon />
                        </Fab>
                    </Box>
                </If>
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth="sm" keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}
